<template>
  <Teleport to="body">
    <v-dialog
      v-for="modal in modals"
      :key="modal.id"
      :model-value="true"
      :width="boundedWidth(modal.options.width)"
      :max-width="boundedWidth(modal.options.maxWidth)"
      :fullscreen="modal.options.fullscreen"
      :persistent="modal.options.persistent || !modal.options.closeOnClickOutside"
      :scrollable="modal.options.scrollable"
      :scroll-strategy="modal.options.fullscreen ? 'none' : 'block'"
      :transition="modal.options.fullscreen ? 'modal-fade' : 'dialog-transition'"
      @update:model-value="handleDialogClose(modal.id, $event)"
      @keydown.esc="handleEsc(modal)"
    >
      <v-card :class="{ 'modal-fullscreen-card': modal.options.fullscreen && modal.options.hideHeader }">
        <template v-if="!modal.options.hideHeader">
          <v-card-title class="d-flex align-center">
            <v-icon
              v-if="modal.options.icon"
              :icon="modal.options.icon"
              size="small"
              class="mr-2"
            />
            <div class="modal-heading">
              <span class="text-h6">{{ modal.title }}</span>
              <p v-if="modal.options.subtitle" class="text-body-2 text-medium-emphasis mb-0">
                {{ modal.options.subtitle }}
              </p>
            </div>
            <v-spacer />
            <v-btn
              v-if="modal.options.closable"
              :icon="mdiClose"
              variant="text"
              size="small"
              @click="handleClose(modal.id)"
            />
          </v-card-title>

          <v-divider />
        </template>

        <v-card-text
          :class="[
            (modal.options.hideHeader || modal.options.noPadding) ? 'pa-0' : 'pa-4',
            { 'modal-fullscreen-content': modal.options.fullscreen && modal.options.hideHeader }
          ]"
        >
          <ModalProvider
            :modal-id="modal.id"
            :close="(data) => handleClose(modal.id, data)"
            :confirm="(data) => handleConfirm(modal.id, data)"
          >
            <component
              :is="modal.component"
              v-bind="modal.options.props"
              @close="handleClose(modal.id)"
              @dismiss="handleClose(modal.id)"
            />
          </ModalProvider>
        </v-card-text>
      </v-card>
    </v-dialog>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { mdiClose } from '@mdi/js'
import { useModalStore } from '../../stores/useModalStore'
import ModalProvider from './ModalProvider.vue'
import type { Modal } from '../../types/modal.types'

const store = useModalStore()

/**
 * The element that opened each modal, so focus can go back to it.
 *
 * `v-dialog` leaves focus on `<body>` when it closes, which drops a keyboard
 * user at the top of the page — the further they were down it, the worse. Hosts
 * were working around it one by one; it belongs here, where every modal of the
 * app passes.
 */
const triggers = new Map<string, HTMLElement>()

async function restoreFocus(trigger: HTMLElement): Promise<void> {
  await nextTick()
  // Gone with a navigation, or something deliberately took the focus — a
  // follow-up dialog, an editor. Only an abandoned focus is ours to move.
  if (!trigger.isConnected) return
  const active = document.activeElement
  if (active && active !== document.body) return
  trigger.focus()
}

// Layouts nest (app layout + settings layout + standalone views), so several
// containers can be mounted at once. Only the primary one draws the modals.
const containerId = ref<string | null>(null)

onMounted(() => {
  containerId.value = store.registerContainer()
})

onBeforeUnmount(() => {
  if (containerId.value) {
    store.unregisterContainer(containerId.value)
    containerId.value = null
  }
})

const isPrimary = computed(
  () => !!containerId.value && store.primaryContainerId === containerId.value
)

const modals = computed(() => (isPrimary.value ? store.modals : []))

/**
 * Keep a fixed pixel width from winning over the screen.
 *
 * Vuetify writes `width` and `max-width` as inline styles on the overlay
 * content, which beats its own responsive `max-width: calc(100% - 48px)`. A
 * dialog opened at, say, 560px therefore rendered 560px wide inside a 374px
 * phone viewport: it overflowed on both sides, putting the secondary action
 * fully off-screen and cutting the primary one — with no way to read or close
 * it. Callers had been told the framework folded the box on small screens; it
 * does not, once an explicit width is passed.
 *
 * Only absolute lengths get the ceiling. Percentages, viewport units and `auto`
 * already scale, and `min()` on them would change a deliberate choice.
 */
function boundedWidth(value: string | number | undefined): string | undefined {
  if (value === undefined || value === null || value === '') return undefined
  const raw = typeof value === 'number' ? `${value}px` : String(value).trim()
  if (!/^\d+(\.\d+)?px$/.test(raw)) return raw
  // 16px of breathing room each side, and never wider than asked.
  return `min(${raw}, calc(100vw - 32px))`
}

// Declared after `modals` on purpose: the getter runs at once, and reading a
// `const` declared further down would throw before the component ever renders.
watch(
  () => modals.value.map((modal) => modal.id),
  (ids, previousIds) => {
    // Recorded before the dialog mounts and takes the focus for itself.
    for (const id of ids) {
      if (previousIds?.includes(id)) continue
      const active = document.activeElement
      if (active instanceof HTMLElement && active !== document.body) triggers.set(id, active)
    }

    for (const id of previousIds ?? []) {
      if (ids.includes(id)) continue
      const trigger = triggers.get(id)
      triggers.delete(id)
      if (trigger) void restoreFocus(trigger)
    }
  },
)

function handleClose(id: string, data?: unknown) {
  store.closeById(id, data)
}

function handleConfirm(id: string, data?: unknown) {
  store.confirmById(id, data)
}

function handleDialogClose(id: string, value: boolean) {
  if (!value) {
    handleClose(id)
  }
}

function handleEsc(modal: Modal) {
  if (modal.options.closeOnEsc && modal.options.closable) {
    handleClose(modal.id)
  }
}
</script>

<style scoped lang="scss">
// v-card-title is nowrap + ellipsis by default, which truncates any subtitle
// longer than the dialog. The heading block wraps on its own instead.
.modal-heading {
  min-width: 0;
  white-space: normal;

  .text-h6 {
    display: block;
    line-height: 1.3;
  }
}

.modal-fullscreen-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.modal-fullscreen-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
</style>

<style lang="scss">
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
